import { AreaChart, Box, Button, Flex, Loading, Text, useAppTheme } from 'ui'
import { useMemo } from 'react'
import { useShiftNoticeTimeseries } from 'queries/pulse/useShiftNoticeTimeseries'
import { usePulseContext } from '../../context/PulseProvider'
import { formatPercentage } from 'lib/string'
import { groupBy, kebabCase } from 'lodash'
import { useShiftNoticeTimeseriesDownloadCsv } from 'queries/pulse/useShiftNoticeTimeseriesDownloadCsv'
import { formatTimePeriodDate } from 'pages/PulsePage/utils'

export function ShiftNoticeTimeseries() {
  const { colors } = useAppTheme()
  const {
    state: { filters },
  } = usePulseContext()

  const { data, isLoading } = useShiftNoticeTimeseries(filters)

  const { mutateAsync: downloadCsv, isPending: isDownloadingCsv } =
    useShiftNoticeTimeseriesDownloadCsv(filters)

  const chartSeries = useMemo(
    () => [
      {
        name: kebabCase('0-6 hours'),
        label: '0-6 hours',
        color: colors.info100,
      },
      {
        name: kebabCase('6-12 hours'),
        label: '6-12 hours',
        color: '#7950f2', // violet-6
      },
      {
        name: kebabCase('12-24 hours'),
        label: '12-24 hours',
        color: colors.error100,
      },
      {
        name: kebabCase('24-48 hours'),
        label: '24-48 hours',
        color: '#be4bdb', // grape-6
      },
      {
        name: kebabCase('48-72 hours'),
        label: '48-72 hours',
        color: colors.warning100,
      },
      {
        name: kebabCase('72+ hours'),
        label: '72+ hours',
        color: colors.success100,
      },
    ],
    []
  )

  const chartData = useMemo(() => {
    const groupedByDate = groupBy(data ?? [], 'shift_date')
    return Object.entries(groupedByDate).map(([date, items]) => {
      const total = items.reduce(
        (acc, item) => acc + item.workers_needed_final,
        0
      )

      return {
        shift_date: formatTimePeriodDate(filters.timePeriod, date),
        ...items.reduce((acc, item) => {
          acc[kebabCase(item.notice_category)] =
            item.workers_needed_final / total
          return acc
        }, {}),
      }
    })
  }, [data])

  return (
    <Box>
      <Flex
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        gap={3}
        mb={3}
      >
        <Box>
          <Text fontSize={3} fontWeight="500">
            Shift Notice Distribution
          </Text>
          <Text fontSize={2} color="lightText">
            Distribution of shift notice
          </Text>
        </Box>
        <Button
          variant="secondary"
          onClick={() => downloadCsv()}
          loading={isDownloadingCsv}
        >
          CSV
        </Button>
      </Flex>
      {isLoading ? (
        <Box p={3}>
          <Loading />
        </Box>
      ) : (
        <AreaChart
          data={chartData}
          dataKey="shift_date"
          type="percent"
          height={450}
          series={chartSeries}
          valueFormatter={formatPercentage}
          withTooltip
          withLegend
          withPointLabels
          withXAxis
          withYAxis
          shouldConnectNulls
          withGradient={false}
        />
      )}
    </Box>
  )
}
